export default ` # Linking Navigation with MovieList

Now, that Genre Selection box and the Sliders work smoothly, we can start shortly browsing movies! Yay!

The \`Movies\` component fetches movies from the TMDB API using the long URL:
\`\`\`plainJavascript
https://api.themoviedb.org/3/discover/movie?api_key=${process.env
  .REACT_APP_TMDB_API_KEY}&language=en-US&sort_by=popularity.desc&include_adult=false&include_video=false&page=1
\`\`\`

Now, we are going to learn how to build an url that includes parameters set by the sliders.
The TMDB API documentation allows us to embed the following parameters:
- with_genres - id of the selected genre or genres, for example 38 or [12, 38, 4561]
- primary_release_date.lte - less than or equal (lte) to the release date, for example "2017-12-31"
- primary_release_date.gte - greater than or equal (gte) to the release date, for example "2010-01-01"
- vote_average.gte - minimum average rating, for example 7
- vote_average.lte - maximum average rating, for example 10
- with_runtime.gte - minimum runtime in minutes, for example 30
- with_runtime.lte - maximum runtime in minutes, for example 120

At the moment, we are storing selected values of the sliders in the \`state\` of the \`Navigation\` component in the \`value\` property.

When we move any of the sliders, the relevant \`onChange\` method is called to set a new \`value\` in the \`state\` of the \`Navigations\` component.

At the same time, it's the \`Movies\` component that actually uses the \`url\` to fetch movies.

That means that we have to find a way to build a communication channel between the \`Navigation\` and \`Movies\` components.

As I mentioned before, in React, communication between components is unidirectional. That's why we have to lift up the state from the \`Navigation\`
component to the \`Main\` component.

Once the \`state\` is in the \`Main\` component, we will pass the necessary information to the \`Navigation\` and the \`Movies\` components via props.

Now, that we have all the information about the selected genres and sliders positions in the \`Main\` component we can build an url there and then pass it
as a \`prop\` to the \`Movies\` component.

## Moving the State to The \`Main\` Component.

First, move the whole \`state\` from the \`Navigation\` component to the \`Main\` component.
Next, move the \`onGenreChange\` and \`onChange\` methods there too.

Finally, in the \`Main\` component, pass the \`state\` and both methods back to the \`Navigation\` component:

\`\`\`plainJsx
// Main.js

<Navigation
  onChange={this.onChange}
  onGenreChange={this.onGenreChange}
  {...this.state}
/>
\`\`\`

In the code above, we have passed \`{...this.state}\` using the ES6 spread operator. It means that all properties of \`this.state\`
will be accessible in the \`Navigation\` component via \`props\`.

Before we moved the \`state\` and the methods from the \`Navigation\` component to the \`Main\` component, we could have used \`this.onChange\`
and \`this.state.year\` in the \`render\` method of the \`Navigation\` component as well.

Now, the \`Navigation\` component receives both the methods and the properties via \`props\`.
So, we can access them using \`this.props.onChange\` and \`this.props.year\` respectively. Or we can destructure \`this.props\` and use \`onChange\` and \`year\`.

Here is the new version of the \`render\` method:

\`\`\`plainJavascript
// Navigation.js

class Navigation extends React.Component {
  // omitted code

  render() {
    const { genre, genres, onGenreChange, onChange, year, rating, runtime } = this.props;
    return (
      <section className="navigation">
        <Selection
          genre={genre}
          genres={genres}
          onGenreChange={onGenreChange}
        />

        <Slider data={year} onChange={onChange} />
        <Slider data={rating} onChange={onChange} />
        <Slider data={runtime} onChange={onChange} />
      </section>
    )
  }
}
\`\`\`

We are still fetching and storing genres in the \`componentDidMount\` method of the \`Navigation\` component.

Since we moved the \`state\`
to the \`Main\` component, we have to create a method in the \`Main\` component that will store genres in the \`state\` of the \`Main\` component
and pass it down to the \`Navigation\` component.

Let's call it \`setGenres\`:

\`\`\`plainJsx
// Main.js

setGenres = genres => {
  this.setState({genres});
}
\`\`\`

We will now pass it to the \`Navigation\` component:
\`\`\`plainJsx
// Main.js

<Navigation
  onChange={this.onChange}
  onGenreChange={this.onGenreChange}
  setGenres={this.setGenres}
  {...this.state}
/>
\`\`\`

Finally, in the \`componentDidMount\` method of the \`Navigation\` component,
we have to replace the previously used \`this.setState({ genres: data.genres })\` with \`this.props.setGenres(data.genres)\`.

This is what the \`componentDidMount\` will look like:

\`\`\`plainJsx
// Navigation.js

componentDidMount() {
  fetch(this.props.url)
    .then(response => response.json())
    .then(data => this.props.setGenres( data.genres ))
    .catch(error => console.log(error));
}
\`\`\`

Now, when we receive data from the TMDB API, we will call the \`setGenres\` method passing \`data.genres\` as an argument
and the \`setGenres\` method will store genres in the \`state\` of the \`Main\` component.

This is what the application with a lifted state should look like:

\`\`\`codeSandbox
92k6lw4lyr
\`\`\`

## The Search Button

Now, we have to add the search button to the \`Navigation\` container. When clicked, it should create a new URL based on the selected genre and
the positions of the sliders. Then, it should store the new URL as the \`moviesUrl\` in the \`state\` of the \`Main\` component.
movies data.

Finally, the \`Main\` container will pass the \`moviesUrl\` to the \`Movies\` component. And the \`Movies\` component will re-fetch the movies and display them.

Let's start by adding the button.

Create two new files in the *navigation* directory – *SearchButton.js* and *SearchButton.css*.

\`\`\`plainJsx
// SearchButton.js

import React from "react";
import "./SearchButton.css"

const SearchButton = ({ onClick }) => (
  <div className="search-button">
    <button onClick={onClick}>Search</button>
  </div>
)

export default SearchButton;
\`\`\`

\`\`\`plainCss
/* SearchButton.css */

.search-button {
  display: flex;
  justify-content: center;
}
.search-button button {
  padding: 10px 21px;
  background: #e27a3f;
  color: rgba(255,255,255, 0.75);
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.2s ease-out;
  outline: 0;
  border: 0;
}

.search-button button:hover {
  color: white;
}
\`\`\`

And import the \`SearchButton\` component into the *Navigation.js* and display it:

\`\`\`plainJsx
// Navigation.js

import SearchButton from './SearchButton';

class Navigation extends React.Component {
  // omitted code

  render() {
    const { genre, genres, onGenreChange, onChange, year, rating, runtime } = this.props;
    return (
      <section className="navigation">
        <Selection
          genre={genre}
          genres={genres}
          onGenreChange={onGenreChange}
        />

        <Slider data={year} onChange={onChange} />
        <Slider data={rating} onChange={onChange} />
        <Slider data={runtime} onChange={onChange} />

        <SearchButton />

      </section>
    )
  }
}
\`\`\`

We now have the button on the screen. But it's dumb. We haven't connected any \`onClick\` method to it.

Let's create the \`onSearchButtonClick\` method in the \`Main\` component and pass it to the
\`Navigation\` component. Then, in the \`Navigation\` component we will assign it to the \`onClick\` event handler of the \`SearchButton\`:

\`\`\`plainJsx
<SearchButton onClick={this.props.onSearchButtonClick}/>
\`\`\`

When we will click on the Button we want it to generate a new url. We will have to create two methods in the \`Main\` container.

One that will generate an url using information from the \`state\`. We will call it \`generateUrl\`.

And the second one, the \`onSearchButtonClick\`.

To generate an url we have to combine the \`year\`, \`rating\` and \`runtime\` ranges, and also \`genreId\`.

We store \`genre\` in the \`state\` of the \`Main\` as the actual name of the genre, for example "Action",
and the \`genres\` array that consists of objects in the following format:

\`\`\`plainJsx
{
  id: 28,
  name: "Action"
}
\`\`\`

To find the appropriate genre object, we will use the \`find\` method.
\`\`\`plainJsx
const selectedGenre = this.state.genres.find( genre => genre.name === this.state.genre );
\`\`\`

Once we find the genre object, we will grab its \`id\`, and we will be ready to generate a url. Here are our two methods:

\`\`\`plainJsx
// Main.js

generateUrl = () => {
    const {genres, year, rating, runtime } = this.state;
    const selectedGenre = genres.find( genre => genre.name === this.state.genre);
    const genreId = selectedGenre.id;

    const moviesUrl = \`https://api.themoviedb.org/3/discover/movie?\` +
      \`api_key=\${process.env.REACT_APP_TMDB_API_KEY}&\` +
      \`language=en-US&sort_by=popularity.desc&\` +
      \`with_genres=\${genreId}&\` +
      \`primary_release_date.gte=\${year.value.min}-01-01&\` +
      \`primary_release_date.lte=\${year.value.max}-12-31&\` +
      \`vote_average.gte=\${rating.value.min}&\` +
      \`vote_average.lte=\${rating.value.max}&\` +
      \`with_runtime.gte=\${runtime.value.min}&\` +
      \`with_runtime.lte=\${runtime.value.max}&\` +
      \`page=1&\`;

    this.setState({ moviesUrl });
  }

  onSearchButtonClick = () => {
    this.generateUrl();
  }
\`\`\`

Now, every time, we click the \`SearchButton\` the new \`moviesUrl\` will be stored in the \`state\`.

We forgot to initialise the \`moviesUrl\` in the \`state\`. It should have the same initial value as the \`apiUrl\` we used in
the \`componentDidMount\` method of the \`Movies\` component:

\`\`\`plainJavascript
// Main.js

state = {
  // omitted code
  moviesUrl: \`https://api.themoviedb.org/3/discover/movie?api_key=\${process.env.REACT_APP_TMDB_API_KEY}&language=en-US&sort_by=popularity.desc&include_adult=false&include_video=false&page=1\`
}
\`\`\`

Now, every time the \`state\` changes, React will pass the new \`moviesUrl\` to the \`Movies\` component. We have to pass the \`moviesUrl\` now as a prop:

\`\`\`plainJsx
// Main.js

class Main extends React.Component {
  // omitted code

  render() {
    return (
      <section className="main">
        // omitted code

        <Movies url={this.state.moviesUrl}/>
      </section>
    )
  }
}
\`\`\`

Then, we have to tweak the \`Movies\` component. It should use the url received via \`props\`.

Until now, we have used the \`componentDidMount\` method to fetch movies. But the \`componentDidMount\` method is called only once – when the component renders for the first time.

If we want to re-fetch movies later, when we receive a new url from the \`Main\` component, we have to use
a different lifecycle function – \`componentWillReceiveProps\`.

Let's move the fetching logic to the new \`fetchMovies\` method so we can call it from both lifecycle methods:

\`\`\`plainJsx
// Movies.js

class Movies extends React.Component {
  // omitted code

  componentDidMount() {
    this.fetchMovies(this.props.url);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.url !== nextProps.url) {
      this.fetchMovies(nextProps.url);
    }
  }

  fetchMovies = (url) => {
    fetch(url)
      .then(response => response.json())
      .then(data => this.storeMovies(data))
      .catch(error => console.log(error));
  }

  // omitted code

}
\`\`\`

Now, when the component renders for the first time, the \`componentDidMount\` method will call the \`fetchMovies\` method passing to it the \`url\` from the \`props\`.

When we choose a different genre or select a different year of production, rating or runtime range, the \`componentWillReceiveProps\` method will be called.

By default, it receives the new \`props\` from the \`Main\` component. At this point in time, we have an access to both – the old props object via \`this.props\` and the new
props object via \`nextProps\`. That's why we can check if the old url is different from the new url. If it is different, we will re-fetch the movies.

Here is the final code:
\`\`\`codeSandbox
kokjvo1z7v
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/link-navigation-movie-list.js